// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import { jwtDecode } from "jwt-decode";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('user'));
  const [redirectPath, setRedirectPath] = useState('/');
  const [sessionTimeout, setSessionTimeout] = useState(null);
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('user');
    setRedirectPath('/');
    setSessionTimeout(null);
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  };

  useEffect(() => {
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        if (decodedToken.exp * 1000 < Date.now()) {
          logout();
          return;
        }
        setUser(decodedToken);

        const expiresIn = decodedToken.exp * 1000 - Date.now();

        intervalRef.current = setInterval(() => {
          const remaining = decodedToken.exp * 1000 - Date.now();
          if (remaining > 0) {
            setSessionTimeout(remaining);
          } else {
            logout();
          }
        }, 1000);

        timeoutRef.current = setTimeout(() => {
          logout();
        }, expiresIn);
      } catch (error) {
        console.error("Invalid token:", error);
        logout();
      }
    } else {
      // No token, ensure everything is cleared
      logout();
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [token]);

  const login = (credentialResponse) => {
    const credential = credentialResponse.credential;
    setToken(credential);
    localStorage.setItem('user', credential);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, redirectPath, setRedirectPath, sessionTimeout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const ProtectedRoute = ({ children }) => {
  const { token, setRedirectPath } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!token) {
      setRedirectPath(location.pathname);
      navigate('/login');
    }
  }, [token, navigate, location, setRedirectPath]);

  return token ? children : null;
};